import { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, getMe } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const userData = await getMe();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await apiLogin(username, password);
    localStorage.setItem('token', response.access_token);
    setUser(response.user);
    setIsAuthenticated(true);
    return response;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Role helpers
  const isSuperAdmin = () => user?.role === 'super_admin';
  const isDepartmentHead = () => user?.role === 'department_head' || user?.role === 'super_admin';
  const isDeputy = () => user?.role === 'deputy';
  const isUser = () => user?.role === 'user';
  const canApprove = () => ['super_admin', 'department_head', 'deputy'].includes(user?.role);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    isSuperAdmin,
    isDepartmentHead,
    isDeputy,
    isUser,
    canApprove
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
